import { ChangeDetectorRef, Component, Directive, ElementRef, Injector, Input, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigStorage, CrudEditorParentService, CrudEditorSelfService, CrudRowsEditConfig, CrudRowsService, CrudRowsTypeInfo, HateoasMultipleResponseObject, isObjectValidator, maxValueValidator, onValueChangeEvent, positiveIntegerRequiredValidator, positiveNonZeroNumberRequiredValidator, requiredIfValidator, requiredValidator, UserStorage } from '@scisys-uim/uim-common';
import { AMMUNITION_STOCK_EVENT_ROWS_TYPE, AMMUNITION_STOCK_METADATA, AMMUNITION_STOCK_TYPE, AmmunitionStock, AmmunitionStockEvent, AmmunitionStockEventType, BUSINESS_UNIT_METADATA, BUSINESS_UNIT_TYPE, BusinessUnit, COST_CENTRE_METADATA, COST_CENTRE_TYPE, COST_CENTRE_TYPE_WILDLIFE, CostCentre, Ranger } from 'assets-model';
import { AmmunitionStockEventRowsService, AmmunitionStockService, BusinessUnitService, CostCentreService, RangerService } from 'assets-service';
import { AmmunitionStockEventRowsComponentBase } from './ammunition-stock-event-rows.component.base';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { RANGER_STATUS_ACTIVE } from 'assets-model';
import moment from 'moment';
import { L } from '@angular/cdk/keycodes';

export interface AmmunitionStockEventRowsEditConfig extends CrudRowsEditConfig<AmmunitionStockEvent, number, any> {
  eventType: AmmunitionStockEventType;
}

@Directive()
export abstract class AmmunitionStockEventRowsComponentCommon<G> extends AmmunitionStockEventRowsComponentBase<G> {
  disabledColumns: string[] = [];
  parentColumns: string[] = [];
  idColumns: string[] = ['ammunitionStockEventId'];

  @Input()
  showIdColumns: boolean = false;

  @Input()
  showParentColumns: boolean = false;

  constructor(protected typeInfo: CrudRowsTypeInfo<AmmunitionStockEvent, number, G>,
    protected ammunitionStockEventService: CrudRowsService<AmmunitionStockEvent, number, G>,
    protected ammunitionStockService: AmmunitionStockService,
    protected rangerService: RangerService,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected configStorage: ConfigStorage,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(typeInfo, ammunitionStockEventService, ammunitionStockService, rangerService, fb, elementRef, router, route, configStorage, userStorage, dialog, injector, snackbar);

    this.unselectedRowsDisabled = true;
    this.clickSelectsRow = true;
    this.resetRowEnabledOnRowSelected = true;
    this.resetRowEnabledOnRowsSet = true;
    this.useCrudEntityArrayForRows = true;
  }

  ngOnInit() {
    super.ngOnInit();

    // if we disable too soon, the column header display text won't be rendered, and the picker will have code names not display names.
    this.columns.$afterViewInit.subscribe(data => {
      this.setDisplayColumnsForContext();
    });
  }

  rowsSet() {
    super.rowsSet();
  }

  private setDisplayColumnsForContext() {
    if (!this.columns) {
      return;
    }
    var disabledColumns = [...this.disabledColumns];
    if (!this.showParentColumns) {
      disabledColumns = disabledColumns.concat(this.parentColumns);
    }
    if (!this.showIdColumns) {
      disabledColumns = disabledColumns.concat(this.idColumns);
    }
    this.columns.setColumnsEnabled(disabledColumns, false);
    // reset the defaults for the context so the picker Reset button doesn't re-enable the columns above
    this.columns.resetInitialDisplayColumns();
  }

  setFormRowEnabled(row: AmmunitionStockEvent, rowForm: FormGroup, enable: boolean) {
    if (enable) {
      rowForm.enable();

      const controls = rowForm.controls;
      for (const controlName in controls) {
        const control = controls[controlName];
        var enableControl = true;

        if (controlName == 'ammunitionStockEventId') {
          enableControl = false;
        }

        this.setControlEnabled(control, enableControl);
      }
    } else {
      rowForm.disable();
    }
  }

}

@Component({
  selector: 'app-ammunition-stock-event-rows',
  templateUrl: './ammunition-stock-event-rows.component.html',
  styleUrls: ['./ammunition-stock-event-rows.component.scss'],
  providers: [CrudEditorParentService],
  viewProviders: [CrudEditorSelfService]
})
export class AmmunitionStockEventRowsComponent extends AmmunitionStockEventRowsComponentCommon<any> {

  public eventType: AmmunitionStockEventType;

  displayedColumns: string[] = ['ammunitionStock', 'currentStockAmount', 'quantity', 'eventDate', 'comments', 'actions'];

  businessUnitSelectionList: BusinessUnit[];
  businessUnitFilter: BusinessUnit;
  businessUnitRevertedAfterChange: boolean = false;
  businessUnitSetFromParams: boolean = false;

  costCentreSelectionList: CostCentre[];
  costCentreFilter: CostCentre;
  costCentreRevertedfterChange: boolean = false;
  costCentreSetFromParams: boolean = false;

  rangerRevertedAfterChange: boolean = false;
  rangerFilter: Ranger;
  rangerDefaultDebounceMillis: number = 500;
  @ViewChild('ranger') rangerInput: ElementRef;
  private rangerSelectionListSubject: Subject<Ranger[]> = new BehaviorSubject([]);
  rangerSelectionListObs: Observable<Ranger[]> = this.rangerSelectionListSubject.asObservable();

  businessUnitCostCenterChangedDialogRef;

  today = moment().endOf('day').toDate();

  get usedAmmunitionStockIds() {
    if (this.rows) {
      return this.rows.filter(row => row.ammunitionStock != null).map(row => row.ammunitionStock.ammunitionStockId);
    }
  }

  get eventDateLabel() {
    if (this.eventType != null) {
      return this.eventType + " Date";
    } else {
      return "Event Date";
    }
  }

  ammunitionStockAddedFromQueryParams: boolean = false;

  constructor(protected ammunitionStockEventService: AmmunitionStockEventRowsService,
    protected ammunitionStockService: AmmunitionStockService,
    protected businessUnitService: BusinessUnitService,
    protected costCentreService: CostCentreService,
    protected rangerService: RangerService,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected configStorage: ConfigStorage,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar,
    private changeDetectorRef: ChangeDetectorRef) {
    super(AMMUNITION_STOCK_EVENT_ROWS_TYPE, ammunitionStockEventService, ammunitionStockService, rangerService, fb, elementRef, router, route, configStorage, userStorage, dialog, injector, snackbar);
    this.unselectedRowsDisabled = false;
    this.clickSelectsRow = true;
  }

  createFormRow(row: AmmunitionStockEvent): FormGroup {
    const self = this;
    return this.fb.group({
      ammunitionStockEventId: [row.ammunitionStockEventId, []],
      ammunitionStock: [row.ammunitionStock, [requiredValidator, isObjectValidator]],
      ranger: [row.ranger, []],
      ammunitionStockEventType: [row.ammunitionStockEventType, [requiredValidator]],
      comments: [row.comments, []],
      eventDate: [row.eventDate, [requiredValidator]],
      createdBy: [row.createdBy, []],
      createdDate: [row.createdDate, []],
      quantity: [row.quantity, [(this.eventType == 'Stocktake' ? positiveIntegerRequiredValidator : positiveNonZeroNumberRequiredValidator), maxValueValidator(() => self.quantityMax(row), () => self.quantityValidatorText(row))]]
    }, {
      validators: [requiredIfValidator(() => self.rowForm(row)?.get('comments'), () => self.stocktakeQuantityNotConsistent(row), "Comments", { required: "Comments are required where there is a discrepancy between stocktake and current stock amount" })]
    });
  }

  createForm(): FormGroup {
    var form = super.createForm();
    form.addControl('businessUnit', this.fb.control(null, [requiredValidator]));
    form.addControl('costCentre', this.fb.control(null, [requiredValidator]));
    form.addControl('ranger', this.fb.control(null, [isObjectValidator]));
    form.setValidators(
      [
        requiredIfValidator(() => this.ranger, () => this.requiresRanger(), "Ranger", { required: "Ranger is Required for Issuing or Returning Ammunition" }),
      ]
    );
    return form;
  }

  createNewItem(): AmmunitionStockEvent {
    var item = super.createNewItem();
    item.ammunitionStockEventType = this.eventType;
    item.eventDate = new Date();
    if (this.requiresRanger()) {
      item.ranger = this.rangerFilter;
    }
    return item;
  }

  addAllWarnings(): void {
    super.addAllWarnings();
    this.addWarningDynamic(() => "You must select a Business Unit and Cost Centre to add rows", () => !this.isAddEnabled());
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.businessUnitLoadSelectionList();
    this.today = moment().endOf('day').toDate();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.wireRangerAutocomplete();
  }

  setDefaultDisabledFields(): void {
    super.setDefaultDisabledFields();
    this.costCentre.disable();
    this.ranger.disable();
  }

  isPersistenceEnabled(): boolean {
    return super.isPersistenceEnabled() && (this.eventType != 'Issue' || this.isRangerFilterPopulated());
  }

  isRangerFilterPopulated() {
    return this.rangerFilter != null && typeof this.rangerFilter != 'string' && this.rangerFilter?.rangerId != null;
  }

  stocktakeQuantityNotConsistent(element: AmmunitionStockEvent) {
    if (this.eventType == 'Stocktake') {
      //if no stocktake, then we can't compare
      if (!element.ammunitionStock?.hasStocktake) {
        return false;
      }
      //if the stocktake quantity is different to the current stock amount, then it's not consistent
      return element.quantity != element.ammunitionStock.currentStockAmount;
    }
    return false;
  }

  quantityRowChanged(value: number, element: AmmunitionStockEvent, index: number, unsetDependents?: boolean): void {
    super.quantityRowChanged(value, element, index, unsetDependents);
    var rowForm = this.rowForm(element);
    if (rowForm) {
      rowForm.get('comments')?.updateValueAndValidity();
      if (this.isLifecycleStateRowsSetComplete() && value != 0) {
        rowForm.get('comments')?.markAsTouched();
      }
    }
  }

  commentsRowChanged(value: string, element: AmmunitionStockEvent, index: number, unsetDependents?: boolean): void {
    super.commentsRowChanged(value, element, index, unsetDependents);
    var rowForm = this.rowForm(element);
    if (rowForm) {
      rowForm.get('comments')?.updateValueAndValidity();
    }
  }

  quantityMax(element: AmmunitionStockEvent): number {
    if (element.ammunitionStock != null) {
      if (this.eventType == 'Issue') {
        return element.ammunitionStock.currentStockAmount ? element.ammunitionStock.currentStockAmount : 0;
      } else if (this.eventType == 'Addition' && !element.ammunitionStock.hasStocktake) {
        return -1;
      }
    }
    return Number.MAX_VALUE;
  }

  quantityValidatorText(element: AmmunitionStockEvent) {
    if (this.eventType == 'Issue' || this.eventType == 'Addition') {
      if (!element.ammunitionStock?.hasStocktake) {
        return "No stocktake has been made for this ammunition stock. You must perform an initial stocktake";
      }
    }
    if (this.eventType == 'Issue') {
      return "Quantity must not exceed the current stock amount of " + this.quantityMax(element);
    }
  }

  allFiltersPopulated() {
    return this.businessUnitFilter != null && this.costCentreFilter != null && (this.eventType != 'Issue' || this.isRangerFilterPopulated());
  }

  isCopyCommentsEnabled(element: AmmunitionStockEvent) {
    return element?.comments != null && element?.comments != "";
  }

  copyComments(element: AmmunitionStockEvent) {
    this.rows.forEach(row => {
      row.comments = element.comments;
      this.rowForm(row)?.get('comments')?.setValue(element.comments);
    });
  }

  isCopyDateEnabled(element: AmmunitionStockEvent) {
    return element?.eventDate != null && (moment.isMoment(element.eventDate) || element.eventDate instanceof Date);
  }

  copyDate(element: AmmunitionStockEvent) {
    this.rows.forEach(row => {
      row.eventDate = element.eventDate;
      this.rowForm(row)?.get('eventDate')?.setValue(element.eventDate);
    });
  }

  isAddEnabled(): boolean {
    return this.allFiltersPopulated() && !this.allAmmunitionTypesAdded();
  }

  allowsAddAllAmmunition(): boolean {
    return !this.allAmmunitionTypesAdded() && this.ammunitionStockSelectionList?.length > 0;
  }

  allAmmunitionTypesAdded(): boolean {
    return this.rows?.length == this.ammunitionStockSelectionList?.length && this.ammunitionStockSelectionList?.length > 0 &&
      this.rows.filter(r => r.ammunitionStock != null).length == this.ammunitionStockSelectionList?.length;
  }

  addAllAmmunition(): void {
    var missingAmmunitionStock = []
    for (let ammunitionStock of this.ammunitionStockSelectionList) {
      var found = false;
      for (let row of this.rows) {
        if (AMMUNITION_STOCK_METADATA.equals(row.ammunitionStock, ammunitionStock)) {
          found = true;
          break;
        }
      }
      if (!found && (this.eventType == 'Stocktake' || ammunitionStock.hasStocktake)) {
        missingAmmunitionStock.push(ammunitionStock);
      }
    }
    var rowForms = [];
    missingAmmunitionStock.forEach(ammunitionStock => {
      var row = this.addNewRow();
      row.ammunitionStock = ammunitionStock;
      row.quantity = 0;
      rowForms.push(this.rowForm(row));
    });
    rowForms.forEach(rf => rf?.markAsUntouched());
    this.snackbar.open("Added " + missingAmmunitionStock.length + " rows", "OK", { duration: 2000 });
  }

  ammunitionStockSelectionListFiltered(element: AmmunitionStockEvent): AmmunitionStock[] {
    if (this.ammunitionStockSelectionList == null) {
      return [];
    }
    return this.ammunitionStockSelectionList.filter(a => (this.eventType == 'Stocktake' || a.hasStocktake) && (!this.usedAmmunitionStockIds.includes(a.ammunitionStockId) || element.ammunitionStock?.ammunitionStockId == a.ammunitionStockId));
  }


  ammunitionStockLoadSelectionList() {
    if (this.businessUnitFilter != null && this.costCentreFilter != null) {
      var filters = {
        buId: this.businessUnitFilter?.buId,
        ccId: this.costCentreFilter?.ccId,
        closed: false,
      }
      this.ammunitionStockService.findByFullFilters(filters, null, true).subscribe(
        data => {
          this.ammunitionStockSelectionList = data.items;
          if (!this.ammunitionStockAddedFromQueryParams && this.routeQueryParams.ammunitionStockId != null) {
            this.ammunitionStockSelectionList.forEach(ammunitionStock => {
              if (ammunitionStock.ammunitionStockId == this.routeQueryParams.ammunitionStockId) {
                var row = this.addNewRow();
                row.ammunitionStock = ammunitionStock;
                row.quantity = 0;
                this.rowForm(row)?.markAsUntouched();
              }
            });
            this.ammunitionStockAddedFromQueryParams = true;
          }
        }
      );
    } else {
      this.ammunitionStockSelectionList = [];
    }
  }

  ammunitionStockDisplay(item: AmmunitionStock): string {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return AMMUNITION_STOCK_TYPE.descriptionSmall(item);
  }

  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }

  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        this.businessUnitSelectionList = data.items;
        if (!this.businessUnitSetFromParams && this.routeQueryParams.businessUnitId != null) {
          this.businessUnitSelectionList.forEach(bu => {
            if (bu.buId == this.routeQueryParams.businessUnitId) {
              this.businessUnitFilter = bu;

            }
          })
        }
        this.businessUnitSetFromParams = true;
      }
    );
  }

  businessUnitChanging(value: BusinessUnit, unsetDependents: boolean = true) {
    if (this.costCentreFilter != null && this.rows.length > 0 && !this.businessUnitRevertedAfterChange) {
      this.confirmBusinessUnitCostCentreChange(true, true);
    }
  }

  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    if (value != null) {
      this.costCentreFilter = null;
      this.costCentreLoadSelectionList();
      this.rangerSelectionListFilter(null);
      this.costCentre.enable();
      this.ranger?.enable();
    } else {
      this.costCentreFilter = null;
      this.costCentre.disable();
      this.ranger?.disable();
    }
    this.businessUnitRevertedAfterChange = false;
  }

  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }

  costCentreDisplay(item: CostCentre) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return COST_CENTRE_TYPE.description(item);
  }

  costCentreLoadSelectionList() {
    if (this.businessUnitFilter != null) {
      this.costCentreService.findByBusinessUnitForType(this.businessUnitFilter.buId, COST_CENTRE_TYPE_WILDLIFE).subscribe(
        data => {
          this.costCentreSelectionList = data.items;
          if (this.costCentreSelectionList.length == 1) {
            this.costCentreFilter = this.costCentreSelectionList[0];
          }
          if (!this.costCentreSetFromParams && this.routeQueryParams.costCentreId != null) {
            this.costCentreSelectionList.forEach(cc => {
              if (cc.ccId == this.routeQueryParams.costCentreId) {
                this.costCentreFilter = cc;
              }
            })
          }
          this.costCentreSetFromParams = true;
        }
      );
    } else {
      this.costCentreSelectionList = [];
    }
  }

  costCentreChanging(value: CostCentre, unsetDependents: boolean = true) {
    if (this.costCentreFilter != null && this.rows.length > 0 && this.businessUnitFilter != null && !this.costCentreRevertedfterChange) {
      this.confirmBusinessUnitCostCentreChange(false, true);
    }
  }

  costCentreChanged(value: CostCentre, unsetDependents: boolean = true) {
    if (value != null) {
      this.ammunitionStockLoadSelectionList();
      this.rangerSelectionListFilter(null);
    }
    if (!this.rangerRevertedAfterChange && !this.costCentreCompare(this.rangerFilter?.costCentre, value)) {
      this.rangerFilter = null;
    }
    this.rangerRevertedAfterChange = false;
    this.costCentreRevertedfterChange = false;
  }

  costCentreCompare(a: any, b: any) {
    return COST_CENTRE_METADATA.equals(a, b);
  }

  confirmBusinessUnitCostCentreChange(businessUnitChanged: boolean, costCentreChanged: boolean) {
    var preChangeBusinessUnit = this.businessUnitFilter;
    var preChangeCostCentre = this.costCentreFilter;
    var preChangeRanger = this.rangerFilter;
    //only open dialog if one is not already open, prevents chain of calls from changing bu (which then changes CC)
    if (this.businessUnitCostCenterChangedDialogRef != null) {
      return;
    }
    this.businessUnitCostCenterChangedDialogRef = this.openSimpleConfirmationDialog("Changing the Business Unit or Cost Centre will clear the current rows. Are you sure you want to continue?", "Change Cost Centre", "OK");
    this.businessUnitCostCenterChangedDialogRef.afterClosed().subscribe(
      result => {
        if (result === true) {
          while (this.rows.length > 0) {
            this.deleteRow(this.rows[0]);
          }
        } else if (result === false) {
          if (businessUnitChanged) {
            this.businessUnitFilter = preChangeBusinessUnit;
            this.businessUnitRevertedAfterChange = true;
          }
          if (costCentreChanged) {
            this.costCentreFilter = preChangeCostCentre;
            this.costCentreRevertedfterChange = true;
          }
          this.rangerFilter = preChangeRanger;
          this.rangerRevertedAfterChange = true;
        } else {
          //neither, dismiss diaog
        }
        setTimeout(() => { this.businessUnitCostCenterChangedDialogRef = null }, 300); //clear dialogue out after delay so reverted changes settle
      }
    );
  }

  wireRangerAutocomplete(): void {
    if (this.ranger) {
      onValueChangeEvent(this.ranger, value => {
        if (value == "") {
          value = null;
          this.rangerFilter = null;
        }
        this.rangerSelectionListFilter(value);
      }, {
        debounceMillis: this.rangerDefaultDebounceMillis
      });
    }
  }

  rangerSelectionListFilter(text: string) {
    var filters = {
      buId: this.businessUnitFilter?.buId,
      // ccIdOrNull: this.costCentreFilter?.ccId, //no longer restrict to just Rangers in that CC
      status: RANGER_STATUS_ACTIVE,
      includeNational: true,
      includeWildlifeManagerRangers: true
    }
    if (text != null && text != "") {
      filters['name'] = text;
    }
    this.rangerService.findByFullFilters(filters).subscribe(data => {
      this.rangerSelectionListSubject.next(data.items);
    });
  }

  rangerDisplay(item: Ranger) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return item.name + (item.businessUnit?.national ? " (National)" : "") + (item.volunteerRanger ? ' (Volunteer)' : '') + (item.wildlifeManagerRanger ? ' (Wildlife Manager)' : '');
  }

  rangerChanging(value: Ranger, unsetDependents: boolean = true) {
  }

  rangerChanged(value: Ranger, unsetDependents: boolean = true) {
    this.rows.forEach(row => {
      if (this.requiresRanger()) {
        row.ranger = value;
      }
    });
  }

  ammunitionStockEventTrackBy(index: number, item: AmmunitionStockEvent) {
    if (item != null) {
      if (item.ammunitionStockEventId != null) {
        return item.ammunitionStockEventId;
      } else if (item?.ammunitionStock != null) {
        return item?.ammunitionStock?.ammunitionStockId;
      } else {
        return index;
      }
    }
  }

  requiresRanger(): boolean{
    return this.eventType == 'Issue' || this.eventType == 'Return';
  }

  retrieveRows(key: any): Observable<HateoasMultipleResponseObject<AmmunitionStockEvent>> {
    return of({ items: [] });
  }

  get costCentre() {
    return this.form?.get('costCentre')
  }

  get businessUnit() {
    return this.form.get('businessUnit');
  }

  get ranger() {
    return this.form.get('ranger');
  }

}





